/**
 * Base sync manager with common functionality
 */

import { ContactStore, MetaData } from '../ContactStore';
import { ContactNormalizer } from '../ContactNormalizer';
import { SearchIndexManager } from '../SearchIndexManager';
import { SyncProgress, SyncOptions, SyncType, DeltaResponse } from './types';

export abstract class BaseSyncManager {
  protected store: ContactStore;
  protected normalizer: ContactNormalizer;
  protected searchIndexManager: SearchIndexManager;
  protected isSyncing = false;
  protected syncTimeout: NodeJS.Timeout | null = null;

  constructor(isOutlookContext: boolean = true) {
    this.store = new ContactStore();
    this.normalizer = new ContactNormalizer();
    this.searchIndexManager = new SearchIndexManager(this.store, isOutlookContext);
  }

  /**
   * Get the store instance (public access)
   */
  getStore(): ContactStore {
    return this.store;
  }

  /**
   * Get sync state (public access)
   */
  getIsSyncing(): boolean {
    return this.isSyncing;
  }

  /**
   * Get the search index manager instance (public access)
   */
  getSearchIndexManager(): SearchIndexManager {
    return this.searchIndexManager;
  }

  /**
   * Get the sync type for this manager
   */
  protected abstract getSyncType(): SyncType;

  /**
   * Get the metadata key prefix for this sync type
   */
  protected abstract getMetaKey(): string;

  /**
   * Fetch data using delta query
   */
  protected abstract fetchDataDelta(accessToken: string, deltaToken: string, ...args: any[]): Promise<any>;

  /**
   * Fetch data using pagination
   */
  protected abstract fetchDataPage(accessToken: string, skipToken: string, top: number, ...args: any[]): Promise<any>;

  /**
   * Process delta query to get final delta token
   */
  protected abstract processDeltaQuery(accessToken: string, deltaToken: string, ...args: any[]): Promise<DeltaResponse>;

  /**
   * Get the source type for normalization
   */
  protected abstract getSourceType(): string;

  /**
   * Check if bootstrap is complete
   */
  protected async isBootstrapComplete(): Promise<boolean> {
    const meta = await this.store.getMeta();
    const key = this.getMetaKey();
    return meta[`${key}BootstrapComplete` as keyof MetaData] as boolean;
  }

  /**
   * Get delta token
   */
  protected async getDeltaToken(): Promise<string | null> {
    const meta = await this.store.getMeta();
    const key = this.getMetaKey();
    return meta[`${key}DeltaToken` as keyof MetaData] as string | null;
  }

  /**
   * Set delta token
   */
  protected async setDeltaToken(deltaToken: string | null): Promise<void> {
    const meta = await this.store.getMeta();
    const key = this.getMetaKey();
    const updatedMeta = {
      ...meta,
      [`${key}DeltaToken`]: deltaToken
    } as MetaData;
    await this.store.setMeta(updatedMeta);
  }

  /**
   * Mark bootstrap as complete
   */
  protected async markBootstrapComplete(totalCount: number, deltaToken: string | null): Promise<void> {
    const meta = await this.store.getMeta();
    const key = this.getMetaKey();
    const now = new Date().toISOString();
    
    // Handle corporate case (empty key) vs others
    const bootstrapCompleteKey = key === '' ? 'bootstrapComplete' : `${key}BootstrapComplete`;
    const lastFullSyncKey = key === '' ? 'lastFullSync' : `${key}LastFullSync`;
    const deltaTokenKey = key === '' ? 'deltaToken' : `${key}DeltaToken`;
    
    // Handle special case for internal users (uses "TotalUsers" instead of "TotalContacts")
    const totalContactsKey = key === 'internal' ? 'internalTotalUsers' : 
                             key === '' ? 'totalContacts' : `${key}TotalContacts`;
    
    const updatedMeta = {
      ...meta,
      [bootstrapCompleteKey]: true,
      [lastFullSyncKey]: now,
      [deltaTokenKey]: deltaToken,
      [totalContactsKey]: totalCount
    } as any;
    
    console.log(`Marking ${key || 'corporate'} bootstrap complete:`, {
      key: key || 'corporate',
      flagName: bootstrapCompleteKey,
      totalCount,
      hasDeltaToken: !!deltaToken,
      deltaTokenPreview: deltaToken ? deltaToken.substring(0, 50) + '...' : 'none'
    });
    
    await this.store.setMeta(updatedMeta);
    
    // Verify the flag was set
    const verifyMeta = await this.store.getMeta();
    console.log(`Verified ${key || 'corporate'} bootstrap flag:`, verifyMeta[bootstrapCompleteKey as keyof MetaData]);
  }

  /**
   * Clear invalid delta token and fall back to bootstrap sync
   */
  protected async clearInvalidDeltaToken(): Promise<void> {
    const meta = await this.store.getMeta();
    const key = this.getMetaKey();
    
    const updatedMeta: MetaData = {
      ...meta,
      [`${key}DeltaToken`]: null,
      [`${key}LastIncrementalSync`]: null
    };
    
    await this.store.setMeta(updatedMeta);
    console.log(`Cleared invalid ${this.getSyncType()} delta token`);
  }

  /**
   * Update incremental sync metadata
   */
  protected async updateIncrementalSyncMeta(deltaToken: string | null, addedCount: number, deletedCount: number): Promise<void> {
    // Re-read metadata to get latest state including other sync managers' delta tokens
    const latestMeta = await this.store.getMeta();
    const key = this.getMetaKey();
    const now = new Date().toISOString();
    
    // Handle corporate case (empty key) vs others
    const deltaTokenKey = key === '' ? 'deltaToken' : `${key}DeltaToken`;
    const lastIncrementalSyncKey = key === '' ? 'lastIncrementalSync' : `${key}LastIncrementalSync`;
    
    // Handle special case for internal users (uses "TotalUsers" instead of "TotalContacts")
    const totalContactsKey = key === 'internal' ? 'internalTotalUsers' : 
                             key === '' ? 'totalContacts' : `${key}TotalContacts`;
    const bootstrapCompleteKey = key === '' ? 'bootstrapComplete' : `${key}BootstrapComplete`;
    
    const updatedMeta = {
      ...latestMeta,  // Use latest metadata instead of stale meta
      [lastIncrementalSyncKey]: now,
      [deltaTokenKey]: deltaToken || latestMeta[deltaTokenKey as keyof MetaData],
      [totalContactsKey]: (latestMeta[totalContactsKey as keyof MetaData] as number) + addedCount - deletedCount,
      [bootstrapCompleteKey]: true // Ensure bootstrap is marked complete after successful incremental sync
    } as any;
    
    await this.store.setMeta(updatedMeta);
  }

  /**
   * Set sync timeout
   */
  protected setSyncTimeout(timeoutMs: number, onTimeout: () => void): void {
    this.syncTimeout = setTimeout(() => {
      if (this.isSyncing) {
        console.warn(`${this.getSyncType()} sync timeout - resetting sync state`);
        this.isSyncing = false;
        onTimeout();
      }
    }, timeoutMs);
  }

  /**
   * Clear sync timeout
   */
  protected clearSyncTimeout(): void {
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
      this.syncTimeout = null;
    }
  }

  /**
   * Clear sync timeout (public access)
   */
  public clearSyncTimeoutPublic(): void {
    this.clearSyncTimeout();
  }

  /**
   * Build search index after sync
   */
  protected async buildSearchIndex(options: SyncOptions): Promise<void> {
    // Skip building if requested (for parallel sync)
    if (options.skipSearchIndex) {
      console.log(`Skipping search index build for ${this.getSyncType()} sync (will build unified index)`);
      return;
    }

    const { onProgress } = options;
    
    console.log(`Building search index after ${this.getSyncType()} sync...`);
    onProgress?.({
      current: 0,
      total: 0,
      phase: 'search-index',
      message: 'Building search index...'
    });
    
    await this.searchIndexManager.buildIndex();
    console.log('Search index built successfully');
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.clearSyncTimeout();
    this.normalizer.destroy();
  }
}
